import { Component, inject, signal, ViewEncapsulation } from '@angular/core'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { FilterState } from './filter.state'
import { debounceTime, distinctUntilChanged, map, take } from 'rxjs'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Store } from '@ngrx/store'
import {
	AddressActions,
	getAddresses,
	getSelectedAddress,
	getSelectedNeighborhood,
	getVenueName,
	VenueActions,
} from '@monorepo-channels/channels/domain'
import { MatExpansionModule } from '@angular/material/expansion'
import { CommonModule } from '@angular/common'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { CategoriesCuisinesComponent } from './categories-cuisines/categories-cuisines.component'
import { isMobile } from '@monorepo-channels/shared/util-helpers'
import { CitiesComponent } from './cities/cities.component'

@Component({
	standalone: true,
	imports: [
		MatFormFieldModule,
		MatInputModule,
		ReactiveFormsModule,
		MatIconModule,
		MatExpansionModule,
		CommonModule,
		MatProgressSpinnerModule,
		CategoriesCuisinesComponent,
		CitiesComponent,
	],
	selector: 'feature-porto-bank-filter',
	templateUrl: './filter.component.html',
	styleUrls: ['./filter.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class FilterComponent {
	private store = inject(Store)
	private filterState = inject(FilterState)
	isMobile = isMobile()
	public searchControl = new FormControl('', { nonNullable: true })
	public placeholder = 'Faça sua busca'
	categoriesOpen = signal(false)
	citiesOpen = signal(false)

	public selectedAddress$ = this.store.select(getSelectedAddress)
	public selectedNeighborhood$ = this.store.select(getSelectedNeighborhood)
	public filterCount = this.filterState.count
	public addresses$ = this.store.select(getAddresses)
	public cities$ = this.addresses$.pipe(map(addresses => [...new Set(addresses.map(a => `${a.city}`))]))

	public selectedCity$ = this.store
		.select(getSelectedAddress)
		.pipe(map(address => (address ? `${address.city}` : null)))

	constructor() {
		this.store.dispatch(AddressActions.getAddresses())
		this.searchControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(name => {
			this.filterState.searchChange.set(name)
		})

		this.searchControl.valueChanges
			.pipe(debounceTime(500), distinctUntilChanged(), takeUntilDestroyed())
			.subscribe(name => {
				if (name.length === 0) {
					this.store.dispatch(VenueActions.cleanFilterVenuesByName())
				} else if (name.length >= 3) {
					this.store.dispatch(VenueActions.filterVenuesByNameV2({ name }))
				}
			})

		this.store
			.select(getVenueName)
			.pipe(take(1))
			.subscribe({
				next: name => {
					if (name) {
						this.searchControl.setValue(name, { emitEvent: false })
					}
				},
			})
	}

	onCitySelected(cityState: string) {
		if (cityState === CitiesComponent.ALL_CITIES) {
			this.store.dispatch(AddressActions.cleanAddress())
			this.store.dispatch(VenueActions.applyFilter())
			return
		}

		const [city] = cityState.split(' - ')

		this.addresses$.pipe(take(1)).subscribe(addresses => {
			const address = addresses.find(a => a.city === city)

			if (!address) return

			this.store.dispatch(AddressActions.setCurrentAddress({ address }))
			this.store.dispatch(AddressActions.setCurrentNeighborhood({ neighborhood: undefined }))

			this.store.dispatch(VenueActions.applyFilter())
		})
	}

	toggleCategories() {
		this.categoriesOpen.update(v => !v)
		this.citiesOpen.set(false)
	}
}
